import React, { useEffect, useMemo, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Alert from "@mui/material/Alert";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import "./Css/AssessmentCreditnew.css";
import "./Css/SubscribedCard.css";
import SubscribedCard from "./SubscribedCard";
import AssessmentPurchasingsCard from "./components/AssessmentPurchasingsCard";
import { AssessmentCreditService } from "../../../../service/manage/exam_subscription/assessCredit.service.js";
import { ExamTransactionService } from "../../../../service/manage/exam_subscription/examTransaction.service.js";

const MID = 100;
const PARENT_FORM_LIST_ID = 29;


const formatDate = (dateValue) => {
	if (!dateValue) {
		return "-";
	}
	const date = new Date(dateValue);
	if (Number.isNaN(date.getTime())) {
		return "-";
	}
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

const formatDateTime = (dateValue) => {
	if (!dateValue) {
		return "-";
	}
	const date = new Date(dateValue);
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	let hours = date.getHours();
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const ampm = hours >= 12 ? "PM" : "AM";
	hours = hours % 12 || 12;
	const hourString = String(hours).padStart(2, "0");

	return `${year}.${month}.${day} ${hourString}.${minutes} ${ampm}`;
};

const AssessmentCreditnew = (props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [credits, setCredits] = useState([]);
	const [creditStatistics, setCreditStatistics] = useState(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedCredit, setSelectedCredit] = useState(null);
	const [transactions, setTransactions] = useState([]);
	const [activeTab, setActiveTab] = useState(0);

	const userType = sessionStorage.getItem("userType");

	const fetchCreditsByUser = async (userIdOrIds) => {
		const res = await AssessmentCreditService.getAllExamCreditsByUser(MID, userIdOrIds);
		if (res.success) {
			setCredits(res?.data?.credits || []);
			setCreditStatistics(res?.data?.statistics || null);
			return;
		}
		setCredits([]);
		setCreditStatistics(null);
	};

	const fetchParentCredits = async () => {
		const lgUserId = sessionStorage.getItem("manage_user_id");
		const lgUserTableId = sessionStorage.getItem("table_log_id");
		const parentGroupId = sessionStorage.getItem("parent_group_id");
		const parentGroupTableId = sessionStorage.getItem("parent_group_user_table_id");

		const loginUserId = parentGroupId || lgUserId;
		const loginUserTableId = parentGroupTableId || lgUserTableId;

		const response = await AssessmentCreditService.getAllStudentsForParent(
			MID,
			PARENT_FORM_LIST_ID,
			loginUserId,
			loginUserTableId,
			"",
			"parent"
		);

		if (!response.success) {
			setCredits([]);
			setCreditStatistics(null);
			return [];
		}

		const userIds = (response?.data?.students || []).map((student) => student.user_id);
		if (!userIds.length) {
			setCredits([]);
			setCreditStatistics(null);
			return [];
		}

		await fetchCreditsByUser(userIds);
		return userIds;
	};

	const fetchTransactions = async (userId) => {
		const mid = 100;
		const limit = 10;
		const offset = 0;
		const search_term = "";
		const transaction_type = "exam";

		const res = await ExamTransactionService.getAllExamTransactionsByUser(
			mid,
			userId,
			limit,
			transaction_type,
			offset,
			search_term
		);
		if (res.success) {
			setTransactions(res.data.transactions || []);
			return;
		}
		setTransactions([]);
	};

	const handleViewClick = (credit) => {
		setSelectedCredit(credit);
		setShowDetailModal(true);
	};

	const toggleDetailModal = () => {
		setShowDetailModal(!showDetailModal);
	};

	useEffect(() => {
		let mounted = true;

		const loadData = async () => {
			if (!mounted) {
				return;
			}
			setIsLoading(true);
			try {
				if (userType === "parent") {
					const studentIds = await fetchParentCredits();
					await fetchTransactions(studentIds);
				} else {
					const studentUserId = sessionStorage.getItem("manage_user_id");
					if (studentUserId) {
						await fetchCreditsByUser(studentUserId);
						await fetchTransactions(studentUserId);
					}
				}
			} catch (error) {
				setCredits([]);
				setCreditStatistics(null);
				setTransactions([]);
			} finally {
				if (mounted) {
					setIsLoading(false);
				}
			}
		};

		loadData();

		return () => {
			mounted = false;
		};
	}, [userType]);

	const statistics = creditStatistics || {};
	const metricItems = useMemo(
		() => [
			{
				value: statistics.total_credits_spent || 0,
				label: "Used Credits",
				icon: TaskAltOutlinedIcon,
				iconClass: "assessment-creditnew-metric-icon--used",
			},
			{
				value: statistics.total_credits_you_can_use || 0,
				label: "Available Credits",
				icon: AccountBalanceWalletOutlinedIcon,
				iconClass: "assessment-creditnew-metric-icon--available",
			},
			{
				value: statistics.total_expired_available_credits || 0,
				label: "Expired Credits",
				icon: TimerOffOutlinedIcon,
				iconClass: "assessment-creditnew-metric-icon--expired",
			},
			{
				value: statistics.total_credits_bought || 0,
				label: "Total Credits",
				icon: SavingsOutlinedIcon,
				iconClass: "assessment-creditnew-metric-icon--total",
			},
		],
		[statistics]
	);

	const creditHistoryPackages = useMemo(() => {
		return credits.filter((credit) => {
			const isExpired = credit?.expiration_date
				? new Date(credit.expiration_date) < new Date()
				: false;
			const totalCredits = Number(credit?.total_credits || 0);
			const usedCredits = Number(credit?.used_credits || 0);
			const isFinished = totalCredits > 0 && usedCredits >= totalCredits;
			return isExpired || isFinished;
		});
	}, [credits]);

	const activePackages = useMemo(() => {
		return credits.filter((credit) => {
			const isExpired = credit?.expiration_date
				? new Date(credit.expiration_date) < new Date()
				: false;
			return !isExpired;
		});
	}, [credits]);

	return (
		<div className="assessment-creditnew-page assessment-creditnew-container">
			<section className="assessment-creditnew-section" aria-label="credits-overview">
				{isLoading ? (
					<div className="assessment-creditnew-loading">
						<Spinner animation="border" role="status">
							<span className="sr-only">Loading...</span>
						</Spinner>
					</div>
				) : (
					<>
						<div className="assessment-creditnew-summary-row">
							<div className="assessment-creditnew-left-content">
								<div className="assessment-creditnew-header-row">
									<h2 className="assessment-creditnew-title">Available Credits:</h2>
									<p className="assessment-creditnew-total">{statistics.total_credits_you_can_use || 0} Credits</p>
								</div>

								<div className="assessment-creditnew-metrics-grid">
									{metricItems.map((metric, index) => (
										<article className="assessment-creditnew-metric-card" key={`${metric.label}-${index}`}>
											<div className="assessment-creditnew-metric-row">
												<div className={`assessment-creditnew-metric-icon ${metric.iconClass}`}>
													<metric.icon fontSize="small" />
												</div>
												<div className="assessment-creditnew-metric-text">
													<div className="assessment-creditnew-metric-value">{metric.value}</div>
													<div className="assessment-creditnew-metric-label">{metric.label}</div>
												</div>
											</div>
										</article>
									))}
								</div>

								<div className="assessment-creditnew-summary-content">
									<div className="assessment-creditnew-summary-left">
										<h3 className="assessment-creditnew-subtitle">Need more credits?</h3>
											<button type="button" className="btn-3d btn btn-primary btn-sm assessment-creditnew-buy-btn" onClick={() => props.onTabChange(null, 1)}>
											Buy Credits
										</button>
									</div>
									<div className="assessment-creditnew-usage">
										<div className="assessment-creditnew-usage-item">
											<span className="assessment-creditnew-usage-label">This Month</span>
											<strong>{statistics.credits_used_this_month || 0} Credits</strong>
										</div>
										<div className="assessment-creditnew-usage-item">
											<span className="assessment-creditnew-usage-label">This Year</span>
											<strong>{statistics.credits_used_this_year || 0} Credits</strong>
										</div>
									</div>
								</div>
							</div>
							<img
								src="/assets/media/bg/çreditboy.png"
								alt="Credits illustration"
								className="assessment-creditnew-image"
							/>
						</div>
					</>
				)}
			</section>

			<hr className="assessment-creditnew-divider" />

			<div>
				<nav className="assessment-creditnew-tabs-container">
					<button
						className={`assessment-creditnew-tab ${activeTab === 0 ? 'assessment-creditnew-tab--active' : ''}`}
						onClick={() => setActiveTab(0)}
					>
						Active Packages
					</button>
					<button
						className={`assessment-creditnew-tab ${activeTab === 1 ? 'assessment-creditnew-tab--active' : ''}`}
						onClick={() => setActiveTab(1)}
					>
						Credit History
					</button>
					<button
						className={`assessment-creditnew-tab ${activeTab === 2 ? 'assessment-creditnew-tab--active' : ''}`}
						onClick={() => setActiveTab(2)}
					>
						Assessment Purchasings
					</button>
				</nav>

				{/* Active Packages Tab */}
				<section className={`assessment-creditnew-section assessment-creditnew-tab-content ${activeTab === 0 ? 'assessment-creditnew-tab-content--active' : ''}`} aria-label="active-packages">
					{isLoading ? (
						<div className="assessment-creditnew-loading">
							<Spinner animation="border" role="status">
								<span className="sr-only">Loading...</span>
							</Spinner>
						</div>
					) : activePackages.length === 0 ? (
						<p className="assessment-creditnew-empty">No subscription package data found.</p>
					) : (
						<div className="assessment-creditnew-subscription-grid subscribed-card-grid">
							{activePackages.map((credit) => {
								return (
									<SubscribedCard
										key={credit.credit_id || `${credit.package_name}-${credit.expiration_date}`}
										credit={credit}
										formatDate={formatDate}
										onViewDetails={() => handleViewClick(credit)}
									/>
								);
							})}
						</div>
					)}
				</section>

				{/* Credit History Tab */}
				<section className={`assessment-creditnew-section assessment-creditnew-tab-content ${activeTab === 1 ? 'assessment-creditnew-tab-content--active' : ''}`} aria-label="credit-history">
					{isLoading ? (
						<div className="assessment-creditnew-loading">
							<Spinner animation="border" role="status">
								<span className="sr-only">Loading...</span>
							</Spinner>
						</div>				) : creditHistoryPackages.length === 0 ? (
					<Alert severity="info" sx={{ margin: "12px 0" }}>
						No expired or finished credit packages found.
					</Alert>					) : (
						<div className="assessment-creditnew-history-list">
							{creditHistoryPackages.map((credit) => {
								const totalCredits = Number(credit?.total_credits || 0);
								const usedCredits = Number(credit?.used_credits || 0);
								const availableCredits = Number(
									credit?.available_credits != null
										? credit.available_credits
										: totalCredits - usedCredits
								);
								const isExpired = credit?.expiration_date
									? new Date(credit.expiration_date) < new Date()
									: false;
								const amountPaid = String(credit?.amount_Paid || "0").replace(/^Rs\.?\s*/i, "");
								const currency = credit?.currency || credit?.currency_type || "LKR";
								const paidBy = credit?.paid_By || credit?.paid_by || "-";

								return (
									<article className="assessment-creditnew-history-card" key={credit.credit_id || `${credit.package_name}-${credit.expiration_date}`}>
										<div className="assessment-creditnew-history-main">
											<div className="assessment-creditnew-history-top">
												<h3 className="assessment-creditnew-history-title">
													{credit?.package_name || "Credit Package"}
													<span className={`assessment-creditnew-history-title-status ${isExpired ? "assessment-creditnew-history-title-status--expired" : "assessment-creditnew-history-title-status--finished"}`}>
														{isExpired ? " - Expired" : " - Finished"}
													</span>
												</h3>
											</div>
											<div className="assessment-creditnew-history-details">
												<div className="assessment-creditnew-history-detail-item">
													<span className="assessment-creditnew-history-detail-label">Total:</span>
													<span className="assessment-creditnew-history-detail-value">{totalCredits}</span>
												</div>
												<div className="assessment-creditnew-history-detail-item">
													<span className="assessment-creditnew-history-detail-label">Used:</span>
													<span className="assessment-creditnew-history-detail-value">{usedCredits}</span>
												</div>
												<div className="assessment-creditnew-history-detail-item">
													<span className="assessment-creditnew-history-detail-label">Available:</span>
													<span className="assessment-creditnew-history-detail-value">{availableCredits > 0 ? availableCredits : 0}</span>
												</div>
												<div className="assessment-creditnew-history-detail-item">
													<span className="assessment-creditnew-history-detail-label">Amount:</span>
													<span className="assessment-creditnew-history-detail-value">{amountPaid} {currency}</span>
												</div>
												<div className="assessment-creditnew-history-detail-item">
													<span className="assessment-creditnew-history-detail-label">Paid By:</span>
													<span className="assessment-creditnew-history-detail-value">{paidBy}</span>
												</div>
												<div className="assessment-creditnew-history-detail-item">
												<span className="assessment-creditnew-history-detail-label">Expired at:</span>
													<span className="assessment-creditnew-history-detail-value">{formatDate(credit?.expiration_date)}</span>
												</div>
											</div>
										</div>
										<div className="assessment-creditnew-history-actions">
											<Button
												type="button"
												variant="outline-secondary"
												className="assessment-creditnew-history-view-btn"
												onClick={() => handleViewClick(credit)}
											>
												View
											</Button>
										</div>
									</article>
								);
							})}
						</div>
					)}
				</section>

				{/* Assessment Purchasings Tab */}
				<section className={`assessment-creditnew-section assessment-creditnew-tab-content ${activeTab === 2 ? 'assessment-creditnew-tab-content--active' : ''}`} aria-label="assessment-purchasings">
					{isLoading ? (
						<div className="assessment-creditnew-loading">
							<Spinner animation="border" role="status">
								<span className="sr-only">Loading...</span>
							</Spinner>
						</div>
					) : transactions.length === 0 ? (
						<p className="assessment-creditnew-empty">No assessment purchasing data found.</p>
					) : (
						<div className="assessment-creditnew-transaction-grid">
							{transactions.map((transaction, index) => (
								<AssessmentPurchasingsCard
									key={transaction.exam_transaction_id || transaction.confirmation_no || `${transaction.created_at}-${index}`}
									transaction={transaction}
									formatDateTime={formatDateTime}
									userType={userType}
								/>
							))}
						</div>
					)}
				</section>
			</div>

			<Modal
				size="lg"
				centered
				show={showDetailModal}
				onHide={toggleDetailModal}
			>
				<Modal.Header>
					<Modal.Title>Transaction Details</Modal.Title>
					<Button variant="secondary" onClick={toggleDetailModal}>
						Close
					</Button>
				</Modal.Header>
				{selectedCredit && (
					<Modal.Body>
						{/* Find the corresponding transaction for this credit */}
						{(() => {
							const transaction = transactions.find(t =>
								t.confirmation_no === selectedCredit.confirmation_no ||
								t.transaction_id === selectedCredit.transaction_id ||
								(t.amount && selectedCredit.amount_Paid && parseFloat(t.amount) === parseFloat(selectedCredit.amount_Paid.replace('Rs.', '')))
							);

							if (transaction) {
								return (
									<div className="transaction-details-grid">
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Amount Paid:</span>
												<span className="detail-value">Rs.{transaction.amount || "0.0"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Confirmation No:</span>
												<span className="detail-value">{transaction.confirmation_no || "N/A"}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Exp. Date:</span>
												<span className="detail-value">{formatDateTime(transaction.expiration_date)}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">No of Credits:</span>
												<span className="detail-value">{transaction.no_of_credits || selectedCredit.total_credits || 0}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Paid By:</span>
												<span className="detail-value">{transaction.paid_by || selectedCredit.paid_By || "N/A"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Payment Method:</span>
												<span className="detail-value">{transaction.payment_method || "N/A"}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Purchase Date:</span>
												<span className="detail-value">{formatDateTime(transaction.purchase_date)}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Reference ID:</span>
												<span className="detail-value">{transaction.reference_id || "N/A"}</span>
											</div>
										</div>
									</div>
								);
							} else {
								// Fallback to credit data if no transaction found
								return (
									<div className="transaction-details-grid">
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Package Name:</span>
												<span className="detail-value">{selectedCredit.package_name || "N/A"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Amount Paid:</span>
												<span className="detail-value">Rs.{selectedCredit.amount_Paid || "0.0"}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Confirmation No:</span>
												<span className="detail-value">{selectedCredit.confirmation_no || "N/A"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Exp. Date:</span>
												<span className="detail-value">{formatDateTime(selectedCredit.expiration_date)}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">No of Credits:</span>
												<span className="detail-value">{selectedCredit.total_credits || 0}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Paid By:</span>
												<span className="detail-value">{selectedCredit.paid_By || "N/A"}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Payment Method:</span>
												<span className="detail-value">{selectedCredit.payment_method || "N/A"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Purchase Date:</span>
												<span className="detail-value">{formatDateTime(selectedCredit.purchase_date)}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Reference ID:</span>
												<span className="detail-value">{selectedCredit.reference_id || "N/A"}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Used Credits:</span>
												<span className="detail-value">{selectedCredit.used_credits || 0}</span>
											</div>
										</div>
										<div className="detail-row">
											<div className="detail-item">
												<span className="detail-label">Available Credits:</span>
												<span className="detail-value">{(selectedCredit.available_credits ?? (selectedCredit.total_credits || 0) - (selectedCredit.used_credits || 0)) > 0 ? (selectedCredit.available_credits ?? (selectedCredit.total_credits || 0) - (selectedCredit.used_credits || 0)) : 0}</span>
											</div>
											<div className="detail-item">
												{/* Empty second column for balance */}
											</div>
										</div>
									</div>
								);
							}
						})()}
					</Modal.Body>
				)}
			</Modal>
		</div>
	);
};

export default AssessmentCreditnew;
